// src/services/ComplianceChainService.js
// Aggregates end-to-end compliance chain validation:
//   1. Coverage: every BWRA risk -> which model/control handles it
//   2. Model quality: each covered model's latest validation status
//   3. Overall status: green/yellow/red based on gaps and failures
//
// This is the system's core "Helhetsvalidering" USP — an on-demand or scheduled
// report proving that identified BWRA risks are actually handled AND that the
// models handling them are current and validated.

import bwraService from './BWRAService';
import dataValidationService from './DataValidationService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import complianceApi from '../api/compliance';
import { makeDualWriteMixin, applyDualWriteMixin } from './_dualWriteHelpers';

const HISTORY_KEY = 'compliance_chain_history';
const SCHEDULE_KEY = 'compliance_chain_schedule';
const HISTORY_LIMIT = 100;

// Staleness threshold in days — validations older than this are flagged as stale/yellow
const STALE_VALIDATION_DAYS = 30;

const ACCURACY_FAIL_THRESHOLD = 80; // accuracy below this fails the model quality check

class ComplianceChainService {
  _entityKey(base) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${base}_${entityId}` : base;
  }

  // Core: build a coverage report. Each entry is a risk with its coverage info.
  getCoverageReport() {
    const risks = bwraService.getRisks();
    return risks.map(risk => {
      const autoControls = bwraService.getAutoControlsForRisk(risk.id);
      const manualControls = (risk.controls || []).filter(c => !c.isAutoGenerated);
      const hasAutoControls = autoControls.length > 0;
      const hasManualControls = manualControls.length > 0;
      const covered = hasAutoControls || hasManualControls;

      return {
        riskId: risk.id,
        riskName: risk.name,
        riskCategory: risk.category,
        grossRisk: risk.grossRisk,
        residualRisk: risk.residualRisk,
        covered,
        autoControls: autoControls.map(c => ({
          id: c.id,
          name: c.name,
          module: c.sourceModule,
          linkedModelId: c.linkedModelId,
        })),
        manualControls: manualControls.map(c => ({
          id: c.id,
          name: c.name,
          type: c.type,
          effectiveness: c.effectiveness,
        })),
      };
    });
  }

  // Build a model-quality report: for each unique model referenced in coverage,
  // attach its latest validation status.
  getModelQualityReport() {
    const coverage = this.getCoverageReport();
    const modelMap = new Map();

    coverage.forEach(entry => {
      entry.autoControls.forEach(ctrl => {
        const key = `${ctrl.module}:${ctrl.linkedModelId || ctrl.name}`;
        if (modelMap.has(key)) return;
        const validation = this._getValidationForModel(ctrl.module, ctrl.linkedModelId, ctrl.name);
        const status = this._classifyValidation(validation);
        modelMap.set(key, {
          module: ctrl.module,
          modelId: ctrl.linkedModelId,
          modelName: ctrl.name,
          validation,
          status,
          coveredRisks: [entry.riskId],
        });
      });
    });

    // Collect coveredRisks across all risks
    coverage.forEach(entry => {
      entry.autoControls.forEach(ctrl => {
        const key = `${ctrl.module}:${ctrl.linkedModelId || ctrl.name}`;
        const existing = modelMap.get(key);
        if (existing && !existing.coveredRisks.includes(entry.riskId)) {
          existing.coveredRisks.push(entry.riskId);
        }
      });
    });

    return Array.from(modelMap.values());
  }

  _getValidationForModel(module, modelId, modelName) {
    if (module === 'GeoRisk') {
      return dataValidationService.getLatestValidationSummary('georisk');
    }
    if (module === 'CustomerRisk') {
      const summary = dataValidationService.getModelValidationSummary('customerrisk', modelName);
      return summary || dataValidationService.getLatestValidationSummary('customerrisk');
    }
    if (module === 'TransactionMonitoring') {
      return dataValidationService.getLatestValidationSummary('transactionmonitoring');
    }
    return null;
  }

  _classifyValidation(validation) {
    if (!validation) return { level: 'grey', reason: 'no_validation' };
    const ageDays = validation.timestamp
      ? Math.floor((Date.now() - new Date(validation.timestamp).getTime()) / (1000 * 60 * 60 * 24))
      : null;
    if (ageDays !== null && ageDays > STALE_VALIDATION_DAYS) {
      return { level: 'yellow', reason: 'stale', ageDays };
    }
    if (validation.accuracy !== undefined && validation.accuracy !== null
        && validation.accuracy < ACCURACY_FAIL_THRESHOLD) {
      return { level: 'red', reason: 'accuracy_low', accuracy: validation.accuracy };
    }
    return { level: 'green', reason: 'ok' };
  }

  // Aggregate overall status. Returns { status, counts, issues[] }.
  getOverallStatus() {
    const coverage = this.getCoverageReport();
    const models = this.getModelQualityReport();

    const uncovered = coverage.filter(r => !r.covered);
    const failedModels = models.filter(m => m.status.level === 'red');
    const staleModels = models.filter(m => m.status.level === 'yellow');
    const missingValidation = models.filter(m => m.status.level === 'grey');

    let level = 'green';
    if (uncovered.length > 0 || failedModels.length > 0) {
      level = 'red';
    } else if (staleModels.length > 0 || missingValidation.length > 0) {
      level = 'yellow';
    }

    const issues = [
      ...uncovered.map(r => ({
        type: 'uncovered_risk',
        level: 'red',
        riskId: r.riskId,
        riskName: r.riskName,
        riskCategory: r.riskCategory,
      })),
      ...failedModels.map(m => ({
        type: 'failed_model',
        level: 'red',
        module: m.module,
        modelName: m.modelName,
        accuracy: m.validation?.accuracy,
      })),
      ...staleModels.map(m => ({
        type: 'stale_validation',
        level: 'yellow',
        module: m.module,
        modelName: m.modelName,
        ageDays: m.status.ageDays,
      })),
      ...missingValidation.map(m => ({
        type: 'missing_validation',
        level: 'yellow',
        module: m.module,
        modelName: m.modelName,
      })),
    ];

    return {
      level,
      counts: {
        totalRisks: coverage.length,
        coveredRisks: coverage.length - uncovered.length,
        uncoveredRisks: uncovered.length,
        totalModels: models.length,
        okModels: models.filter(m => m.status.level === 'green').length,
        staleModels: staleModels.length,
        failedModels: failedModels.length,
        missingValidationModels: missingValidation.length,
      },
      issues,
    };
  }

  // Run the full validation: persist a history snapshot with timestamp
  async runValidation(triggeredBy = 'manual') {
    const now = new Date().toISOString();
    const overall = this.getOverallStatus();
    const entry = {
      timestamp: now,
      triggeredBy,
      status: overall.level,
      counts: overall.counts,
      issues: overall.issues,
    };
    const history = this.getHistory();
    const newHistory = [entry, ...history].slice(0, HISTORY_LIMIT);
    await this._dualWriteJson(HISTORY_KEY, newHistory);
    return entry;
  }

  getHistory() {
    return storage.getJSON(this._entityKey(HISTORY_KEY), []);
  }

  getLastRun() {
    const history = this.getHistory();
    return history.length > 0 ? history[0] : null;
  }

  // Scheduled run configuration
  getSchedule() {
    return storage.getJSON(this._entityKey(SCHEDULE_KEY), {
      enabled: false,
      frequency: 'daily', // 'daily' | 'weekly' | 'monthly'
      hour: 6,
    });
  }

  async saveSchedule(schedule) {
    await this._dualWriteJson(SCHEDULE_KEY, schedule);
  }

  // Check whether a scheduled run is due, and if so, run it. Called on app init
  // by the Dashboard — this is an in-app polling approximation (for production
  // a server-side job runner is required, but this covers daily/weekly user
  // workflows in a single-tenant SPA context).
  async runIfDue() {
    const schedule = this.getSchedule();
    if (!schedule.enabled) return null;
    const lastRun = this.getLastRun();
    const now = new Date();
    if (!lastRun) {
      return await this.runValidation('scheduled');
    }
    const lastTime = new Date(lastRun.timestamp);
    const ageMs = now.getTime() - lastTime.getTime();
    const day = 24 * 60 * 60 * 1000;
    let threshold;
    switch (schedule.frequency) {
      case 'weekly': threshold = 7 * day; break;
      case 'monthly': threshold = 30 * day; break;
      case 'daily':
      default: threshold = day; break;
    }
    if (ageMs < threshold) return null;
    return await this.runValidation('scheduled');
  }

  // INFRA-181: backend dual-write + initFromBackend.
  async initFromBackend() {
    return this._initFromBackend([HISTORY_KEY, SCHEDULE_KEY], []);
  }
}

applyDualWriteMixin(ComplianceChainService, makeDualWriteMixin({
  storage, api: complianceApi, serviceName: 'ComplianceChainService',
}));

const complianceChainService = new ComplianceChainService();
export default complianceChainService;
